import type { Row, Table } from "@tanstack/react-table";

import type { FileItem } from "../../types";

import { AssetGrid, FolderGrid, GroupHeader } from "./components";

type FilesGridViewProps = {
  isLoading: boolean;
  table: Table<FileItem>;
};

function getGroupSelectionState(groupRow: Row<FileItem>) {
  const fileRowsInGroup = groupRow.subRows.filter(
    (row) => row.original.type === "file",
  );

  const allSelected = fileRowsInGroup.every((row) => row.getIsSelected());
  const someSelected =
    fileRowsInGroup.some((row) => row.getIsSelected()) && !allSelected;

  const toggleGroupSelection = () => {
    // If all selected or some selected, deselect all. Otherwise, select all.
    if (allSelected || someSelected) {
      for (const row of fileRowsInGroup) row.toggleSelected(false);
    } else {
      for (const row of fileRowsInGroup) row.toggleSelected(true);
    }
  };

  return { allSelected, someSelected, toggleGroupSelection };
}

export function FilesGridView({ isLoading, table }: FilesGridViewProps) {
  const isEmpty = table.getGroupedRowModel().rows.length === 0;

  if (isLoading) {
    return (
      <div className="overflow-y-auto max-h-[calc(100vh-310px)]">
        <div className="flex h-full w-full items-center justify-center text-text-secondary">
          {/* TODO: add loader */}
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="overflow-y-auto max-h-[calc(100vh-310px)]">
        <div className="flex h-full w-full items-center justify-center text-text-secondary">
          {/* TODO: update copy */}
          <span>This folder is empty. Upload files to get started.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-y-auto max-h-[calc(100vh-310px)]">
      <div className="flex flex-col gap-md">
        {table
          .getGroupedRowModel()
          .rows.sort((a, b) => {
            const aType = a.getValue("type") as "file" | "folder";
            const bType = b.getValue("type") as "file" | "folder";

            if (aType === "folder" && bType === "file") {
              return -1;
            }
            if (aType === "file" && bType === "folder") {
              return 1;
            }

            return 0;
          })
          .map((groupRow) => {
            // Get the group name (type)
            const groupType = groupRow.getValue("type") as "file" | "folder";
            const groupTitle = groupType === "folder" ? "Folders" : "Assets";
            const { allSelected, someSelected, toggleGroupSelection } =
              getGroupSelectionState(groupRow);

            return (
              <div className="flex flex-col gap-md" key={groupRow.id}>
                <GroupHeader
                  allSelected={allSelected}
                  groupTitle={groupTitle}
                  onToggleSelection={toggleGroupSelection}
                  showCheckbox={groupType === "file"}
                  someSelected={someSelected}
                />

                {groupType === "folder" ? (
                  <FolderGrid
                    resetRowSelection={table.resetRowSelection}
                    resetSorting={table.resetSorting}
                    rows={groupRow.subRows}
                  />
                ) : (
                  <AssetGrid
                    resetRowSelection={table.resetRowSelection}
                    rows={groupRow.subRows}
                  />
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
